import axios, { type AxiosError } from 'axios'
import { ApiError, type ApiErrorResponse } from '../types/api.types'
import { useAuthStore } from '../store/authStore'

/**
 * Centralized Axios HTTP client for NTTD.
 *
 * ALL backend communication must go through this client.
 * Components, hooks, and pages MUST NOT import axios directly.
 *
 * What this client handles:
 * 1. Base URL from environment variable
 * 2. Attach JWT access token from memory on every request
 * 3. Automatic silent token refresh on 401 responses
 * 4. Parse all error responses into typed ApiError objects
 * 5. withCredentials: true — allows the browser to send HttpOnly refresh token cookie
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Required for HttpOnly refresh token cookie
  headers: {
    'Content-Type': 'application/json',
  },
})

// ── Request Interceptor: Attach Access Token ──────────────────
apiClient.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Track if we're currently refreshing to prevent parallel refresh calls
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: unknown) => void
  reject: (reason: unknown) => void
}> = []

const processFailedQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })
  failedQueue = []
}

// ── Response Interceptor: Handle Errors + Silent Token Refresh ─
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean }

    // If 401 and we haven't already retried, attempt token refresh for authenticated requests.
    // IMPORTANT: never retry if the failing request is login, register, or refresh —
    // auth failures on those endpoints must be surfaced directly to the UI.
    const isAuthEndpoint =
      originalRequest.url?.includes('/api/auth/refresh') ||
      originalRequest.url?.includes('/api/auth/login') ||
      originalRequest.url?.includes('/api/auth/register')

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Queue requests while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`
          }
          return apiClient(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const refreshUrl = BASE_URL ? `${BASE_URL}/api/auth/refresh` : '/api/auth/refresh'
        const { data } = await axios.post(refreshUrl, {}, {
          withCredentials: true,
        })

        const newToken = data.accessToken
        useAuthStore.getState().setAccessToken(newToken)
        processFailedQueue(null, newToken)

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
        }
        return apiClient(originalRequest)
      } catch (refreshError) {
        // Refresh failed — clear auth and signal AuthContext to redirect.
        // Using a custom event avoids a full page reload (which would remount
        // AuthContext and trigger another refresh attempt).
        processFailedQueue(refreshError as Error, null)
        useAuthStore.getState().clearAuth()
        window.dispatchEvent(new CustomEvent('auth:session-expired'))
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // Parse the error response into a typed ApiError
    if (error.response?.data) {
      const errorData = error.response.data as ApiErrorResponse
      return Promise.reject(new ApiError(errorData, error.response.status))
    }

    // Network or unknown error
    return Promise.reject(
      new ApiError(
        { code: 'NETWORK_ERROR', message: 'Network error. Please check your connection.', timestamp: new Date().toISOString(), path: '', traceId: 'unknown' },
        0
      )
    )
  }
)

export default apiClient
